import { create } from 'zustand';
import { SpaceData, RoomData } from '@/components/SpacePlanningPanel/SpacePlanningTypes';
import { formaStorageService } from '@/services/forma/storage-service';

const STORAGE_KEY = 'sp-storage';

interface SpacePlanningStore {
	spaces: Record<string, SpaceData>;
	loading: boolean;
	error: Error | null;
	isSaving: boolean;

	addRooms: (buildingPath: string, rooms: RoomData[]) => void;
	removeRoom: (buildingPath: string, room: RoomData) => void;
	updateRoom: (buildingPath: string, updatedRoom: RoomData) => void;
	updateSpace: (buildingPath: string, updatedSpace: SpaceData) => void;
	pruneSpacesForBuilding: (buildingPrefix: string, validKeys: string[]) => void;
	renameSpaceKey: (fromKey: string, toKey: string) => void;

	refresh: () => Promise<void>;
	saveNow: () => Promise<void>;
}

export const useSpacePlanningStore = create<SpacePlanningStore>((set) => {
	let saveTimer: NodeJS.Timeout | null = null;
	let pendingData: Record<string, SpaceData> | null = null;
	const debounceMs = 500;

	const debouncedSave = async (dataToSave: Record<string, SpaceData>) => {
		try {
			set({ isSaving: true, error: null });
			await formaStorageService.setJSON(STORAGE_KEY, dataToSave);
		} catch (err) {
			console.error('Error saving spaces:', err);
			set({
				error: err instanceof Error ? err : new Error('Failed to save spaces'),
			});
		} finally {
			set({ isSaving: false });
		}
	};

	const scheduleSave = (data: Record<string, SpaceData>) => {
		pendingData = data;
		if (saveTimer) clearTimeout(saveTimer);
		saveTimer = setTimeout(() => {
			if (pendingData) {
				debouncedSave(pendingData);
				pendingData = null;
			}
		}, debounceMs);
	};

	return {
		spaces: {},
		loading: true,
		error: null,
		isSaving: false,

		refresh: async () => {
			try {
				set({ loading: true, error: null });

				if (pendingData) {
					set({ spaces: pendingData, loading: false });
					return;
				}
				const data =
					await formaStorageService.getJSON<Record<string, SpaceData>>(STORAGE_KEY);
				set({ spaces: data || {}, loading: false });
			} catch (err) {
				console.error('Error loading spaces:', err);
				set({
					error: err instanceof Error ? err : new Error('Failed to load spaces'),
					loading: false,
				});
			}
		},

		saveNow: async () => {
			if (saveTimer) clearTimeout(saveTimer);
			if (pendingData) {
				await debouncedSave(pendingData);
				pendingData = null;
			}
		},

		addRooms: (buildingPath, rooms) => {
			set((state) => {
				const existing = state.spaces[buildingPath] || { tare: 0.15, rooms: [] };
				const updated: Record<string, SpaceData> = {
					...state.spaces,
					[buildingPath]: {
						...existing,
						rooms: [...existing.rooms, ...rooms],
					},
				};
				scheduleSave(updated);
				return { spaces: updated };
			});
		},

		removeRoom: (buildingPath, room) => {
			set((state) => {
				const existing = state.spaces[buildingPath];
				if (!existing) return state;

				const updatedRooms = existing.rooms.filter(
					(r) => String(r.spaceId) !== String(room.spaceId)
				);
				const updated: Record<string, SpaceData> = {
					...state.spaces,
					[buildingPath]: {
						...existing,
						rooms: updatedRooms,
					},
				};
				scheduleSave(updated);
				return { spaces: updated };
			});
		},

		updateRoom: (buildingPath, updatedRoom) => {
			set((state) => {
				const existing = state.spaces[buildingPath];
				if (!existing) return state;

				const updatedRooms = existing.rooms.map((r) => {
					if (r.spaceId !== updatedRoom.spaceId) return r;

					return {
						...r,
						...updatedRoom,
						materials: {
							...r.materials,
							...updatedRoom.materials,
						},
						scoreBreakdown: {
							...r.scoreBreakdown,
							...updatedRoom.scoreBreakdown,
						},
						acoustics: {
							...r.acoustics,
							...updatedRoom.acoustics,
						},
						waterFeatures: {
							...r.waterFeatures,
							...updatedRoom.waterFeatures,
						},
					};
				});
				const updated: Record<string, SpaceData> = {
					...state.spaces,
					[buildingPath]: {
						...existing,
						rooms: updatedRooms,
					},
				};
				scheduleSave(updated);
				return { spaces: updated };
			});
		},

		updateSpace: (buildingPath, updatedSpace) => {
			set((state) => {
				const updated: Record<string, SpaceData> = {
					...state.spaces,
					[buildingPath]: updatedSpace,
				};
				scheduleSave(updated);
				return { spaces: updated };
			});
		},

		pruneSpacesForBuilding: (buildingPrefix, validKeys) => {
			set((state) => {
				const prefix = `${buildingPrefix}-`;
				const validSet = new Set(validKeys);
				const updated = { ...state.spaces };
				let removed = false;

				Object.keys(updated).forEach((key) => {
					if (key.startsWith(prefix) && !validSet.has(key)) {
						delete updated[key];
						removed = true;
					}
				});

				if (!removed) {
					return state;
				}

				scheduleSave(updated);
				return { spaces: updated };
			});
		},

		renameSpaceKey: (fromKey, toKey) => {
			set((state) => {
				if (!fromKey || !toKey || fromKey === toKey) return state;

				const fromSpace = state.spaces[fromKey];
				if (!fromSpace) return state;

				const toSpace = state.spaces[toKey];
				const updated = { ...state.spaces };

				if (!toSpace) {
					updated[toKey] = fromSpace;
				} else {
					const roomMap = new Map<string, RoomData>();
					(toSpace.rooms || []).forEach((room) =>
						roomMap.set(String(room.spaceId), room)
					);
					(fromSpace.rooms || []).forEach((room) => {
						const roomId = String(room.spaceId);
						if (!roomMap.has(roomId)) {
							roomMap.set(roomId, room);
						}
					});

					updated[toKey] = {
						...fromSpace,
						...toSpace,
						rooms: Array.from(roomMap.values()),
					};
				}

				delete updated[fromKey];
				scheduleSave(updated);
				return { spaces: updated };
			});
		},
	};
});
